"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useAlertStatusCounts = exports.useSensorAlert = exports.useSensorAlerts = void 0;
const react_1 = require("react");
const mutations_1 = require("../graphql/mutations");
const queries_1 = require("../graphql/queries");
const subscriptions_1 = require("../graphql/subscriptions");
const useLogger_1 = require("./useLogger");
const amplifyBootstrap_1 = require("../utils/amplifyBootstrap");
const useSensorAlerts = (sensorId) => {
    const [sensorAlerts, setSensorAlerts] = (0, react_1.useState)([]);
    const [loading, setLoading] = (0, react_1.useState)(false);
    const [error, setError] = (0, react_1.useState)(null);
    const subscriptionsRef = (0, react_1.useRef)([]);
    const logger = (0, useLogger_1.useLogger)('useSensorAlerts');
    const fetchAndUpdateSensorAlerts = async (sensorId, limit = 50) => {
        if (!sensorId) {
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const sensorAlerts = await fetchSensorAlerts(limit, sensorId);
            setSensorAlerts(sensorAlerts);
        }
        catch (error) {
            logger.error('Error fetching sensor alerts:', { error });
            setError(error);
            setSensorAlerts([]);
        }
        finally {
            setLoading(false);
        }
    };
    const fetchSensorAlerts = async (limit = 50, sensorId) => {
        try {
            const response = await amplifyBootstrap_1.API.graphql({
                query: queries_1.getSensorAlertsBySensor,
                variables: { sensorId, limit },
            });
            const sensorAlerts = response.data?.getSensorAlertsBySensor || [];
            return sensorAlerts.map(parseSensorAlert);
        }
        catch (error) {
            logger.error('Error fetching sensor alerts:', { error });
            return [];
        }
    };
    const fetchAndUpdateAllSensorAlerts = async (limit = 50, nextToken, filters) => {
        setLoading(true);
        setError(null);
        try {
            const response = await amplifyBootstrap_1.API.graphql({
                query: queries_1.listSensorAlerts,
                variables: {
                    limit,
                    nextToken,
                    customerId: filters?.customerId,
                    alertType: filters?.alertType,
                    severity: filters?.severity,
                    status: filters?.status,
                },
            });
            const responseData = response.data?.listSensorAlerts;
            const pageAlerts = Array.isArray(responseData) ? responseData : responseData?.items || [];
            const sensorAlerts = pageAlerts.map(parseSensorAlert);
            setSensorAlerts(sensorAlerts);
            const paginationNextToken = Array.isArray(responseData) ? undefined : responseData?.nextToken;
            return {
                alerts: sensorAlerts,
                nextToken: paginationNextToken,
                hasMore: !!paginationNextToken,
            };
        }
        catch (error) {
            logger.error('Error fetching all sensor alerts:', { error });
            setError(error);
            setSensorAlerts([]);
            return {
                alerts: [],
                nextToken: undefined,
                hasMore: false,
            };
        }
        finally {
            setLoading(false);
        }
    };
    const clear = () => {
        setSensorAlerts([]);
    };
    const fetchBySensorIdAndSubscribe = () => {
        if (!sensorId) {
            return;
        }
        fetchAndUpdateSensorAlerts(sensorId);
        try {
            const createSubscription = amplifyBootstrap_1.API.graphql({
                query: subscriptions_1.onCreateSensorAlertBySensor,
                variables: { sensorId },
            }).subscribe({
                next: ({ data }) => {
                    const newAlert = data.onSensorSensorAlertCreated;
                    if (newAlert) {
                        setSensorAlerts((prevAlerts) => {
                            const updatedAlerts = [parseSensorAlert(newAlert), ...prevAlerts];
                            return updatedAlerts.slice(0, 100);
                        });
                    }
                },
                error: (error) => console.error('Create sensor alert subscription error:', error),
            });
            const updateSubscription = amplifyBootstrap_1.API.graphql({
                query: subscriptions_1.onUpdateSensorAlertBySensor,
                variables: { sensorId },
            }).subscribe({
                next: ({ data }) => {
                    const updatedAlert = data.onSensorSensorAlertUpdated;
                    if (updatedAlert) {
                        setSensorAlerts((prevAlerts) => {
                            const parsed = parseSensorAlert(updatedAlert);
                            const existingIndex = prevAlerts.findIndex((a) => a.id === parsed.id);
                            if (existingIndex === -1) {
                                return [parsed, ...prevAlerts].slice(0, 100);
                            }
                            const clone = [...prevAlerts];
                            clone[existingIndex] = { ...clone[existingIndex], ...parsed };
                            return clone;
                        });
                    }
                },
                error: (error) => console.error('Update sensor alert subscription error:', error),
            });
            const serviceRequestSubscription = amplifyBootstrap_1.API.graphql({
                query: subscriptions_1.onUpdateServiceRequest,
            }).subscribe({
                next: ({ data }) => {
                    const updatedServiceRequest = data?.onServiceRequestUpdated;
                    if (updatedServiceRequest?.id) {
                        setSensorAlerts((prevAlerts) => prevAlerts.map((alert) => {
                            if (alert.serviceRequest?.id === updatedServiceRequest.id) {
                                return {
                                    ...alert,
                                    serviceRequest: updatedServiceRequest,
                                };
                            }
                            return alert;
                        }));
                    }
                },
                error: (error) => console.error('Service request subscription error:', error),
            });
            subscriptionsRef.current = [createSubscription, updateSubscription, serviceRequestSubscription];
        }
        catch (err) {
            console.error('Error setting up sensor alerts subscription:', err);
        }
    };
    const unsubscribe = () => {
        if (subscriptionsRef.current.length === 0) {
            return;
        }
        try {
            subscriptionsRef.current.forEach((subscription) => {
                if (subscription && typeof subscription.unsubscribe === 'function') {
                    subscription.unsubscribe();
                }
            });
        }
        catch (error) {
            console.error('Error cleaning up sensor alerts subscription:', error);
        }
    };
    (0, react_1.useEffect)(() => {
        if (!sensorId) {
            return;
        }
        unsubscribe();
        fetchBySensorIdAndSubscribe();
        // TODO: this is temporary, remove it when we have proper subscription HB-900 https://treeline.atlassian.net/browse/HB-900
        const interval = setInterval(() => {
            fetchAndUpdateSensorAlerts(sensorId);
        }, 30000);
        return () => {
            clearInterval(interval);
            unsubscribe();
        };
    }, [sensorId]);
    const fetchAllSensorAlertsWithPagination = async (limit = 50, nextToken) => {
        setLoading(true);
        setError(null);
        try {
            const response = await amplifyBootstrap_1.API.graphql({
                query: queries_1.listSensorAlerts,
                variables: { limit, nextToken },
            });
            const responseData = response.data?.listSensorAlerts;
            const pageAlerts = Array.isArray(responseData) ? responseData : responseData?.items || [];
            const sensorAlerts = pageAlerts.map(parseSensorAlert);
            setSensorAlerts(sensorAlerts);
            const paginationNextToken = Array.isArray(responseData) ? undefined : responseData?.nextToken;
            return {
                alerts: sensorAlerts,
                nextToken: paginationNextToken,
                hasMore: !!paginationNextToken,
            };
        }
        catch (error) {
            logger.error('Error fetching paginated sensor alerts:', { error });
            setError(error);
            setSensorAlerts([]);
            return {
                alerts: [],
                nextToken: undefined,
                hasMore: false,
            };
        }
        finally {
            setLoading(false);
        }
    };
    const memoizedSensorAlerts = (0, react_1.useMemo)(() => sensorAlerts, [sensorAlerts]);
    return {
        sensorAlerts: memoizedSensorAlerts,
        loading,
        error,
        fetchSensorAlerts,
        fetchAndUpdateSensorAlerts,
        fetchAndUpdateAllSensorAlerts,
        fetchAllSensorAlertsWithPagination,
        clear,
    };
};
exports.useSensorAlerts = useSensorAlerts;
const useSensorAlert = (id) => {
    const [sensorAlert, setSensorAlert] = (0, react_1.useState)(null);
    const [isFetching, setIsFetching] = (0, react_1.useState)(false);
    const [isUpdating, setIsUpdating] = (0, react_1.useState)(false);
    const [error, setError] = (0, react_1.useState)(null);
    const fetchSensorAlert = async () => {
        try {
            setIsFetching(true);
            const response = await amplifyBootstrap_1.API.graphql({
                query: queries_1.getSensorAlert,
                variables: { id },
            });
            if (response && response.data && response.data.getSensorAlert) {
                setSensorAlert(parseSensorAlert(response.data.getSensorAlert));
            }
            else {
                console.warn('Unexpected API response structure:', response);
                setSensorAlert(null);
            }
            setError(null);
        }
        catch (error) {
            setError(error);
            console.error('Error fetching sensor alert:', error);
        }
        finally {
            setIsFetching(false);
        }
    };
    const updateAlertStatus = async (input) => {
        try {
            setIsUpdating(true);
            const response = await amplifyBootstrap_1.API.graphql({
                query: mutations_1.updateSensorAlertStatus,
                variables: { input },
            });
            const updatedAlert = response.data.updateSensorAlertStatus;
            const parsedSensorAlert = parseSensorAlert(updatedAlert);
            setSensorAlert({ ...sensorAlert, ...parsedSensorAlert });
            return parsedSensorAlert;
        }
        catch (err) {
            console.error('Error updating sensor alert status:', err);
            throw err;
        }
        finally {
            setIsUpdating(false);
        }
    };
    const updateAlertAcknowledgedState = async (input) => {
        try {
            setIsUpdating(true);
            const response = await amplifyBootstrap_1.API.graphql({
                query: mutations_1.updateSensorAlertAcknowledgedState,
                variables: { input },
            });
            const updatedAlert = response.data.updateSensorAlertAcknowledgedState;
            const parsedSensorAlert = parseSensorAlert(updatedAlert);
            setSensorAlert({ ...sensorAlert, ...parsedSensorAlert });
            return parsedSensorAlert;
        }
        catch (err) {
            console.error('Error updating acknowledged state for sensor alert:', err);
            throw err;
        }
        finally {
            setIsUpdating(false);
        }
    };
    (0, react_1.useEffect)(() => {
        if (id) {
            fetchSensorAlert();
        }
    }, [id]);
    return {
        sensorAlert,
        isFetching,
        error,
        isUpdating,
        updateSensorAlertStatus: updateAlertStatus,
        updateSensorAlertAcknowledgedState: updateAlertAcknowledgedState,
    };
};
exports.useSensorAlert = useSensorAlert;
const useAlertStatusCounts = (filters) => {
    const [counts, setCounts] = (0, react_1.useState)(null);
    const [loading, setLoading] = (0, react_1.useState)(false);
    const [error, setError] = (0, react_1.useState)(null);
    const fetchCounts = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await amplifyBootstrap_1.API.graphql({
                query: queries_1.getSensorAlertStatusCounts,
                variables: {
                    customerId: filters?.customerId,
                    alertType: filters?.alertType,
                    severity: filters?.severity,
                },
            });
            if (response?.data?.getSensorAlertStatusCounts) {
                setCounts(response.data.getSensorAlertStatusCounts);
            }
        }
        catch (error) {
            setError(error);
            console.error('Error fetching alert status counts:', error);
        }
        finally {
            setLoading(false);
        }
    };
    (0, react_1.useEffect)(() => {
        fetchCounts();
    }, [filters?.customerId, filters?.alertType, filters?.severity]);
    return {
        counts,
        loading,
        error,
        refetch: fetchCounts,
    };
};
exports.useAlertStatusCounts = useAlertStatusCounts;
const parseSensorAlert = (alert) => {
    let parsedMetadata = {};
    let parsedValue = alert.value;
    if (typeof alert.metadata === 'string') {
        try {
            parsedMetadata = JSON.parse(alert.metadata);
        }
        catch {
            parsedMetadata = { raw: alert.metadata };
        }
    }
    else if (typeof alert.metadata === 'object' && alert.metadata !== null) {
        parsedMetadata = alert.metadata;
    }
    if (typeof alert.value === 'string') {
        try {
            parsedValue = JSON.parse(alert.value);
        }
        catch {
            parsedValue = alert.value;
        }
    }
    // Safely handle parsedValue which might be null/undefined
    const powerMonitorInputNumber = parsedValue && parsedValue.branch && typeof parsedValue.branch === 'number'
        ? parsedValue.branch
        : parsedValue && typeof parsedValue.branch === 'string' && !isNaN(Number(parsedValue.branch))
            ? Number(parsedValue.branch)
            : undefined;
    return {
        ...alert,
        value: parsedValue?.value || parsedValue,
        parsedMetadata,
        powerMonitorInputNumber,
    };
};
